/**
 * Shared primitives for the Web Spreadsheet App.
 *
 * Every coordinate in this module is 0-based internally. A1 notation (1-based
 * rows, letter columns) exists only at the boundary, in `src/core/a1.ts`.
 */

/** Stable sheet identity, independent of the user-visible tab name. */
export type SheetId = string;

/** A single cell address. 0-based row and column. */
export interface Addr {
  sheet: SheetId;
  row: number;
  col: number;
}

/** A rectangular range. Both corners are inclusive. */
export interface Range {
  sheet: SheetId;
  r0: number;
  c0: number;
  r1: number;
  c1: number;
}

/** The seven Excel error values the Formula_Engine can produce. */
export type ErrorCode =
  | '#REF!'
  | '#DIV/0!'
  | '#NAME?'
  | '#VALUE!'
  | '#N/A'
  | '#NUM!'
  | '#NULL!';

/**
 * A stored cell value. Dates, times, percentages, and currency are all
 * `number` plus a number format held by the Format_Manager.
 */
export type CellValue =
  | { k: 'empty' }
  | { k: 'number'; v: number }
  | { k: 'text'; v: string }
  | { k: 'bool'; v: boolean }
  | { k: 'error'; v: ErrorCode; detail?: string; origin?: Addr };

/** Column-level type, as inferred on import and tracked by the Data_Store. */
export type ScalarType = 'empty' | 'number' | 'text' | 'bool' | 'date' | 'mixed';

/** Navigation direction for block-edge movement (Ctrl+Arrow). */
export type Direction = 'up' | 'down' | 'left' | 'right';

/** Hard workbook limits (Requirements 1.1, 1.2, 1.3, 16.1). */
/** Maximum supported worksheet rows: 1 crore + 1,101 rows. */
export const MAX_ROWS = 10_001_101;
export const MAX_COLS = 18_278;
export const MAX_SHEETS = 200;

/** Rows per storage chunk in the columnar Data_Store. */
export const CHUNK_ROWS = 65_536;

/** All error codes, for exhaustive iteration in tests and generators. */
export const ERROR_CODES: readonly ErrorCode[] = [
  '#REF!',
  '#DIV/0!',
  '#NAME?',
  '#VALUE!',
  '#N/A',
  '#NUM!',
  '#NULL!',
];

export const EMPTY: CellValue = { k: 'empty' };

export function isValidRow(row: number): boolean {
  return Number.isInteger(row) && row >= 0 && row < MAX_ROWS;
}

export function isValidCol(col: number): boolean {
  return Number.isInteger(col) && col >= 0 && col < MAX_COLS;
}

/** Normalizes a range so that r0 <= r1 and c0 <= c1. */
export function normalizeRange(r: Range): Range {
  return {
    sheet: r.sheet,
    r0: Math.min(r.r0, r.r1),
    c0: Math.min(r.c0, r.c1),
    r1: Math.max(r.r0, r.r1),
    c1: Math.max(r.c0, r.c1),
  };
}

/** A single-cell range covering `a`. */
export function rangeOfAddr(a: Addr): Range {
  return { sheet: a.sheet, r0: a.row, c0: a.col, r1: a.row, c1: a.col };
}

export function rangeContains(r: Range, a: Addr): boolean {
  const n = normalizeRange(r);
  return a.sheet === n.sheet && a.row >= n.r0 && a.row <= n.r1 && a.col >= n.c0 && a.col <= n.c1;
}
